import React, { useState } from 'react'
import { IQuestionViewProps } from '../../types.ts'
import { EcommerceQuestionModelTypes } from '../types.ts'
import ItemPreview from '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ItemPreview.tsx'
import Item from '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/Item.tsx'

import style from '../style.module.scss'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import Ecommerce from '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Cart/Ecommerce.tsx'

import commonStyle from '../../_common/style.module.scss'
import { formatPriceWithLocale } from '../functions.ts'
import useSurveyLocale from '../../../modules/locale/useSurveyLocale.ts'
import useTimeline from '../../../modules/timeline/useTimeline.ts'

const EcommerceView = ({
  schema,
  response,
  responseValidation,
  // _timeline,
  showValidation,
  onUpdateResponse
}: IQuestionViewProps<EcommerceQuestionModelTypes>) => {
  const productsPerRow = schema.products_per_row || 3
  const viewport = useViewport()
  const timeline = useTimeline()
  const width = viewport.mobile ? viewport.width : viewport.widgetWidth
  const naAnswer = schema.answers.find(a => a.na)
  const naChecked = response.answers.some(r => r.id == naAnswer?.id && r.v > 0)
  const totals = [
    response.answers.reduce((prev, cur) => prev + cur.v, 0),
    response.answers.reduce((prev, cur) => prev + cur.v * cur.price, 0)
  ]
  const { t, languageTag } = useSurveyLocale()
  const selectedIndex = response.answers.findIndex(r => r.selected)
  const [showCart, setShowCart] = useState(false)
  const changeCart = () => {
    if (selectedIndex >= 0) {
      response.answers[selectedIndex].selected = false
      onUpdateResponse(response)
    }
    setShowCart(!showCart)
  }
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const maxQty = findability ? null : schema.max_qty_per_product
  const maxCartSize = findability ? null : schema.max_cart_size
  const maxMoney = findability ? null : schema.max_money_spent
  const maxPrice = schema.answers.reduce(
    (prev, cur) => ((cur.discounted_price || cur.price) > prev ? cur.discounted_price || cur.price : prev),
    0
  )
  const maxProductCount = response.answers.reduce((prev, cur) => (cur.v > prev ? cur.v : prev), 0)

  const renderError = () => {
    if (findability) return ''
    const note = !showCart && selectedIndex < 0 ? t('questions', 'ec', 'note') : ''
    if (!(schema.min_qty || schema.max_cart_size || schema.max_qty_per_product || schema.max_money_spent)) return note

    const error =
      (!schema.min_qty || schema.min_qty <= totals[0]
        ? ''
        : t<(n1: number) => string>('questions', 'ec', 'emptyCartError')(schema.min_qty) + '  ') +
      (!schema.max_cart_size || schema.max_cart_size > totals[0]
        ? ''
        : t<(n1: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size) + '  ') +
      (!schema.max_money_spent || schema.max_money_spent >= totals[1] + maxPrice
        ? ''
        : t<(n1: string) => string>(
            'questions',
            'ec',
            'maxSpentError'
          )(formatPriceWithLocale(schema.max_money_spent, schema.price_mask, languageTag)) + '  ') +
      (!schema.max_qty_per_product || schema.max_qty_per_product > maxProductCount
        ? ''
        : t<(n1: number) => string>('questions', 'ec', 'maxProductCountError')(schema.max_qty_per_product) + '  ')

    return note + '<br/>' + error
  }

  return (
    <div className={style.EcommerceView} data-testid="SW-SH">
      {
        <legend
          // id="checkboxgroup-label"
          className={`
                ${commonStyle.validationMessage}
                ${style.validationMessage}
                ${naChecked ? style.disabled : ''}
                ${showValidation && !responseValidation.valid ? ' ' + commonStyle.error : ''}
              `}
          data-testid="SW-SH-ValidationMessage"
          dangerouslySetInnerHTML={{
            __html: `${renderError()}`
          }}
        ></legend>
      }

      {showCart && selectedIndex < 0 ? (
        <>
          <Ecommerce
            totals={totals}
            response={response}
            schema={schema}
            onUpdateResponse={onUpdateResponse}
            showCart={changeCart}
          />
        </>
      ) : (
        <div className={`${naChecked ? style.disabled : ''}`}>
          {selectedIndex >= 0 ? (
            <Item
              schema={schema.answers[selectedIndex]}
              questionSchema={schema}
              response={response}
              qty={response.answers[selectedIndex].v}
              onClose={() => {
                response.answers[selectedIndex].selected = false
                onUpdateResponse(response)
                timeline.log({
                  type: 'close_popup',
                  ref: response.answers[selectedIndex].id
                })
              }}
              onAddToCart={qty => {
                response.answers[selectedIndex].v += qty || 1
                response.answers[selectedIndex].selected = false
                timeline.log({
                  type: 'add_to_cart',
                  ref: response.answers[selectedIndex].id,
                  target: (qty || 1).toString()
                })
                onUpdateResponse(response)
              }}
              totals={totals}
            />
          ) : (
            <div className={`${style.productList} ${naChecked ? style.disabled : ''}`}>
              {schema.answers
                .filter(answer => !answer.na)
                .map((answer, i) => (
                  <div className={style['divided' + productsPerRow]} key={i}>
                    <ItemPreview
                      schema={answer}
                      questionSchema={schema}
                      onAddToCart={() => {
                        response.answers[i].v += 1
                        timeline.log({
                          type: 'add_to_cart',
                          ref: response.answers[i].id,
                          target: '1'
                        })
                        onUpdateResponse(response)
                      }}
                      onItemSelect={() => {
                        response.answers[i].selected = true
                        response.clicked = true
                        onUpdateResponse(response)
                        timeline.log({
                          type: 'open_popup',
                          ref: response.answers[i].id
                        })
                      }}
                      width={(width as number) / productsPerRow}
                      data-testid="SW-SH-Item"
                      available={
                        (!maxQty || 1 <= maxQty - response.answers[i].v) &&
                        (!maxCartSize || 1 + totals[0] <= maxCartSize) &&
                        (!maxMoney || (answer.discounted_price || answer.price) + totals[1] <= maxMoney)
                      }
                    />
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EcommerceView
